import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Mail,
  Phone,
  Building2,
  Calendar,
  Target,
  TrendingUp,
  IndianRupee,
  Award,
  Loader2,
  AlertCircle,
  Key,
  CheckCircle,
  Shield
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar
} from 'recharts';
import { usersAPI, teamsAPI, incentivesAPI, reportsAPI } from '../../services/api';
import { cn } from '../../lib/utils';
import { useAuth } from '../../contexts/AuthContext';
import { usePrivacy } from '../../contexts/PrivacyContext';

const timeRanges = [
  { value: 'CURRENT_MONTH', label: 'Current Month' },
  { value: 'LAST_MONTH', label: 'Last Month' },
  { value: 'THIS_QUARTER', label: 'This Quarter' },
  { value: 'YEAR_TO_DATE', label: 'Year to Date' },
];

const getRoleBadgeColor = (role) => {
  const colors = {
    ADMIN: 'bg-purple-100 text-purple-700',
    SUB_ADMIN: 'bg-blue-100 text-blue-700',
    SALES_MANAGER: 'bg-green-100 text-green-700',
    EMPLOYEE: 'bg-slate-100 text-slate-700',
    FINANCE_VIEWER: 'bg-amber-100 text-amber-700'
  };
  return colors[role] || 'bg-gray-100 text-gray-700';
};

const UserProfilePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const { formatMoney } = usePrivacy();
  const formatCurrency = formatMoney; // Alias for compatibility
  const [user, setUser] = useState(null);
  const [team, setTeam] = useState(null);
  const [manager, setManager] = useState(null);
  const [incentives, setIncentives] = useState([]);
  const [performanceStats, setPerformanceStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('CURRENT_MONTH');

  // Check if current user is admin
  const isAdmin = currentUser?.role === 'ADMIN';

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Use admin endpoint if current user is admin to get password info
        let userRes;
        if (isAdmin) {
          // Get user from admin list to get password details
          const adminListRes = await usersAPI.listAdmin();
          const foundUser = (adminListRes.data || []).find(u => u.id === id);
          if (foundUser) {
            userRes = { data: foundUser };
          } else {
            userRes = await usersAPI.get(id);
          }
        } else {
          userRes = await usersAPI.get(id);
        }
        setUser(userRes.data);
        
        // Fetch team if user has one
        if (userRes.data?.team_id) {
          try {
            const teamRes = await teamsAPI.get(userRes.data.team_id);
            setTeam(teamRes.data);
          } catch { setTeam(null); }
        }
        
        // Fetch manager if user has one
        if (userRes.data?.manager_id) {
          try {
            const mgrRes = await usersAPI.get(userRes.data.manager_id);
            setManager(mgrRes.data);
          } catch { setManager(null); }
        }
        
        // Fetch incentives
        try {
          const incRes = await incentivesAPI.listRecords({ user_id: id });
          const incData = incRes.data;
          setIncentives(Array.isArray(incData) ? incData : (incData?.records || []));
        } catch { setIncentives([]); }

        // Fetch performance stats from reports API
        try {
          const perfRes = await reportsAPI.performance({ user_id: id });
          const userData = perfRes.data?.data?.find(u => u.user_id === id);
          if (userData) {
            setPerformanceStats(userData);
          }
        } catch { setPerformanceStats(null); }
      } catch (error) {
        console.error('Failed to fetch user:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  // Calculate stats from real data
  const stats = {
    sqd_count: performanceStats?.sqd_count || 0,
    conversion_count: performanceStats?.deals_won || 0,
    revenue_contribution: performanceStats?.deal_value || 0,
    incentive_earned: incentives.reduce((sum, i) => sum + (i.amount_earned || i.amount || 0), 0),
    incentive_paid: incentives.filter(i => i.status === 'PAID').reduce((sum, i) => sum + (i.amount_earned || i.amount || 0), 0),
    incentive_unrealized: incentives.filter(i => i.status !== 'PAID').reduce((sum, i) => sum + (i.amount_earned || i.amount || 0), 0)
  };

  // Performance data for charts (would come from reports API trend endpoint)
  const performanceData = [
    { month: 'Jul', sqds: 0, conversions: 0 },
    { month: 'Aug', sqds: 0, conversions: 0 },
    { month: 'Sep', sqds: 0, conversions: 0 },
    { month: 'Oct', sqds: 0, conversions: 0 },
    { month: 'Nov', sqds: 0, conversions: 0 },
    { month: 'Dec', sqds: stats.sqd_count, conversions: stats.conversion_count },
  ];

  // Recent activity - would come from activity API
  const recentActivity = incentives.slice(0, 4).map(inc => ({
    action: inc.metric_type === 'SQD' ? 'SQD Bonus' : 'Deal Bonus',
    details: `${inc.notes || 'Incentive recorded'} - ${formatCurrency(inc.amount_earned || inc.amount || 0)}`,
    time: inc.created_at ? new Date(inc.created_at).toLocaleDateString() : 'Recently'
  }));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        <span className="ml-2 text-slate-500">Loading profile...</span>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <AlertCircle className="w-12 h-12 text-slate-400 mb-4" />
        <p className="text-lg text-slate-600">User not found</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/team')}>
          Back to Team
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/team')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-slate-700 flex items-center justify-center text-white font-bold text-xl">
              {user.avatar || user.name.split(' ').map(n => n[0]).join('')}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-800">{user.name}</h1>
              <div className="flex items-center gap-2 mt-1">
                <Badge className={getRoleBadgeColor(user.role)}>
                  {user.role.replace('_', ' ')}
                </Badge>
                <Badge className={user.status === 'ACTIVE' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                  {user.status}
                </Badge>
              </div>
            </div>
          </div>
        </div>
        <Select value={timeRange} onValueChange={setTimeRange}>
          <SelectTrigger className="w-[180px] bg-white border-slate-200">
            <Calendar className="w-4 h-4 mr-2 text-slate-400" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {timeRanges.map(range => (
              <SelectItem key={range.value} value={range.value}>
                {range.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Info & Activity */}
        <div className="lg:col-span-2 space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-3 gap-4">
            <Card className="bg-white border-slate-200">
              <CardContent className="p-5">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Target className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">SQDs</p>
                    <p className="text-2xl font-bold text-slate-800">{stats.sqd_count}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white border-slate-200">
              <CardContent className="p-5">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-emerald-100 rounded-lg">
                    <TrendingUp className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Conversions</p>
                    <p className="text-2xl font-bold text-slate-800">{stats.conversion_count}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white border-slate-200">
              <CardContent className="p-5">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <IndianRupee className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Revenue</p>
                    <p className="text-2xl font-bold text-slate-800">{formatCurrency(stats.revenue_contribution)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Performance Chart */}
          <Card className="bg-white border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg">Performance Trend</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={performanceData}>
                    <defs>
                      <linearGradient id="colorSqdsUser" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="month" stroke="#94a3b8" fontSize={12} />
                    <YAxis stroke="#94a3b8" fontSize={12} />
                    <Tooltip />
                    <Area
                      type="monotone"
                      dataKey="sqds"
                      stroke="#0ea5e9"
                      strokeWidth={2}
                      fillOpacity={1}
                      fill="url(#colorSqdsUser)"
                      name="SQDs"
                    />
                    <Area
                      type="monotone"
                      dataKey="conversions"
                      stroke="#10b981"
                      strokeWidth={2}
                      fill="transparent"
                      name="Conversions"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Recent Activity */}
          <Card className="bg-white border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg">Recent Activity</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {recentActivity.map((activity, index) => (
                  <div key={index} className="flex items-start gap-4 p-3 bg-slate-50 rounded-lg">
                    <div className="w-2 h-2 rounded-full bg-blue-500 mt-2" />
                    <div className="flex-1">
                      <p className="font-medium text-slate-800">{activity.action}</p>
                      <p className="text-sm text-slate-500">{activity.details}</p>
                    </div>
                    <span className="text-xs text-slate-400">{activity.time}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Details */}
        <div className="space-y-6">
          {/* Contact Info */}
          <Card className="bg-white border-slate-200">
            <CardHeader>
              <CardTitle className="text-sm">Contact Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-slate-400" />
                <span className="text-sm text-slate-600">{user.email}</span>
              </div>
              <div className="flex items-center gap-3">
                <Building2 className="w-4 h-4 text-slate-400" />
                <span className="text-sm text-slate-600">{team?.team_name || 'No Team'}</span>
              </div>
              {manager && (
                <div className="flex items-center gap-3">
                  <Phone className="w-4 h-4 text-slate-400" />
                  <span className="text-sm text-slate-600">Reports to: {manager.name}</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Password Status - Admin Only */}
          {isAdmin && (
            <Card className="bg-white border-slate-200">
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <Shield className="w-4 h-4 text-slate-500" />
                  Password Status
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-500">Status</span>
                  {user.has_changed_password ? (
                    <Badge className="bg-emerald-100 text-emerald-700">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Password Changed
                    </Badge>
                  ) : (
                    <Badge className="bg-amber-100 text-amber-700">
                      <Key className="w-3 h-3 mr-1" />
                      Initial Password
                    </Badge>
                  )}
                </div>
                {user.password_changed_at && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-500">Changed On</span>
                    <span className="text-sm text-slate-700">
                      {new Date(user.password_changed_at).toLocaleDateString('en-IN', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric'
                      })}
                    </span>
                  </div>
                )}
                {!user.has_changed_password && user.initial_password && (
                  <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <p className="text-xs text-amber-600 mb-1">Initial Password</p>
                    <code className="text-sm font-mono text-amber-800 break-all">
                      {user.initial_password}
                    </code>
                  </div>
                )}
                {user.force_password_change && (
                  <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                    User will be prompted to change password on next login
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Incentives Summary */}
          <Card className="bg-white border-slate-200">
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-2">
                <Award className="w-4 h-4 text-amber-500" />
                Incentives
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                <p className="text-xs text-emerald-600">Total Earned</p>
                <p className="text-xl font-bold text-emerald-700">{formatCurrency(stats.incentive_earned)}</p>
              </div>
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-xs text-blue-600">Paid</p>
                <p className="text-xl font-bold text-blue-700">{formatCurrency(stats.incentive_paid)}</p>
              </div>
              <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-xs text-amber-600">Unrealized</p>
                <p className="text-xl font-bold text-amber-700">{formatCurrency(stats.incentive_unrealized)}</p>
              </div>
            </CardContent>
          </Card>

          {/* Deals & SQDs */}
          <Card className="bg-white border-slate-200">
            <CardHeader>
              <CardTitle className="text-sm">Deals & SQDs</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {incentives.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">No deals or SQDs yet</p>
                ) : incentives.slice(0, 5).map((inc, idx) => (
                  <div key={inc.id || idx} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                    <span className="text-sm text-slate-600">{inc.company_name || 'Unknown'}</span>
                    <Badge className={inc.metric_type === 'SQD' ? 'bg-blue-100 text-blue-700 text-xs' : 'bg-emerald-100 text-emerald-700 text-xs'}>
                      {inc.metric_type || 'Deal'}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default UserProfilePage;
